// Public names stay independent of the original coral/teal/paper theme tokens.
export const BRAND_COLOR_KEYS = Object.freeze(['highlight', 'background', 'surface', 'text'])

export function normalizeBrandColor(value) {
  if (typeof value !== 'string') return ''
  const color = value.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(color)) return color
  if (/^#[0-9a-f]{3}$/.test(color)) return '#' + [...color.slice(1)].map(part => part + part).join('')
  return ''
}

export function normalizeBrandColors(colors) {
  return Object.fromEntries(BRAND_COLOR_KEYS.flatMap(key => {
    const value = normalizeBrandColor(colors?.[key])
    return value ? [[key, value]] : []
  }))
}

function channels(color) {
  return [1, 3, 5].map(index => Number.parseInt(color.slice(index, index + 2), 16))
}

function mix(color, other, amount) {
  const second = channels(other)
  return '#' + channels(color).map((value, index) => Math.round(value * (1 - amount) + second[index] * amount).toString(16).padStart(2, '0')).join('')
}

function luminance(color) {
  const values = channels(color).map(value => {
    const component = value / 255
    return component <= .04045 ? component / 12.92 : ((component + .055) / 1.055) ** 2.4
  })
  return values[0] * .2126 + values[1] * .7152 + values[2] * .0722
}

export function colorContrast(first, second) {
  const a = luminance(first)
  const b = luminance(second)
  return (Math.max(a, b) + .05) / (Math.min(a, b) + .05)
}

export function contrastText(background) {
  return colorContrast(background, '#ffffff') >= colorContrast(background, '#000000') ? '#ffffff' : '#000000'
}

export function createBrandPalette(input = {}, theme = {}) {
  const colors = normalizeBrandColors(input)
  if (typeof theme === 'string') {
    try { theme = JSON.parse(theme) } catch { theme = {} }
  }
  const old = key => normalizeBrandColor(theme?.[key])
  const highlight = colors.highlight || old('accent') || '#8547be'
  const background = colors.background || old('paper') || '#212529'
  const surface = colors.surface || (colors.background ? mix(background, contrastText(background), .045) : old('surface') || '#2a2e33')
  const text = colors.text || (colors.background || colors.surface ? contrastText(surface) : old('ink') || '#eeeeee')
  const customBase = Boolean(colors.background || colors.surface || colors.text)
  let muted = customBase ? mix(surface, text, .72) : old('muted') || '#adb5bd'
  for (let amount = .76; colorContrast(muted, surface) < 4.5 && amount <= 1; amount += .04) muted = mix(surface, text, amount)
  const onHighlight = contrastText(highlight)
  return {
    '--gather-color-highlight': highlight,
    '--gather-color-highlight-hover': (!colors.highlight && old('accentStrong')) || mix(highlight, onHighlight === '#ffffff' ? '#000000' : '#ffffff', .12),
    '--gather-color-on-highlight': onHighlight,
    '--gather-color-highlight-soft': 'color-mix(in srgb, var(--gather-color-highlight) 14%, var(--gather-color-surface))',
    '--gather-color-background': background,
    '--gather-color-surface': surface,
    '--gather-color-surface-alt': customBase ? mix(surface, background, .5) : '#24282d',
    '--gather-color-text': text,
    '--gather-color-text-secondary': customBase ? mix(surface, text, .9) : old('inkSecondary') || '#dadce0',
    '--gather-color-muted': muted,
    '--gather-color-border': customBase ? mix(surface, text, .23) : '#41454c',
    '--gather-color-scheme': luminance(background) > .179 ? 'light' : 'dark',
  }
}

// Use a named layer, not inline attributes: ordinary client CSS wins without
// !important, even when a lazy-loaded application stylesheet arrives later.
export function applyBrandPalette(colors, theme, enabled, doc = document) {
  let element = doc.querySelector('style[data-gather-palette]')
  if (!enabled) { element?.remove(); return {} }
  const palette = createBrandPalette(colors, theme)
  if (!element) {
    element = doc.createElement('style')
    element.dataset.gatherPalette = 'colors'
    doc.head.appendChild(element)
  }
  element.textContent = `@layer gather-theme { .gather-event { ${Object.entries(palette).map(([key, value]) => `${key}: ${value};`).join(' ')} } }`
  return palette
}
